'use client';

import Image from 'next/image';
import Link from 'next/link';

import paths from '@/paths';
import { TTour } from '@/types';

import './css/tourCard.css';
import { LazyImage } from './LazyImage';
import { clock, message, pin, star } from 'public/static/icons';
import { useFeatures } from '../tour/tour-features/hooks/useFeatures';
import { format } from '@/utils';

const MIN_WIDTH = 280;
const MAX_WIDTH = 380;
const HEIGHT = 280;
const DEFAULT_IMG = '/static/images/eagle.svg';

const imageContainerStyle = {
  width: '100%',
  minWidth: `${MIN_WIDTH}px`,
  maxWidth: `${MAX_WIDTH}px`,
  height: `${HEIGHT}px`,
};

export default function TourCard({
  tour,
  lazyLoadImages,
  onClick,
}: {
  tour: TTour;
  lazyLoadImages?: boolean;
  onClick?: () => void;
}) {
  const { primaryLanguage, additionalCount } = format.operatedInSummary(tour.operatedIn);

  const { getFeatureDetail } = useFeatures({ size: 24, color: "#FFF" });

  const feature = tour.tourType ? getFeatureDetail(tour.tourType) : null;

  return (
    <Link href={paths.tourShow(tour.id)} className="flex tour-card" onClick={onClick}>
      <div className="card-container relative flex flex-col justify-start grow-1">
        <div className="image-container" style={imageContainerStyle}>
          {lazyLoadImages ? (
            <LazyImage
              src={tour?.photoUrl || DEFAULT_IMG}
              alt={tour.title}
              containerStyle={imageContainerStyle}
            />
          ) : (
            <img
              src={tour?.photoUrl || DEFAULT_IMG}
              alt={tour.title}
              loading="lazy"
              style={{
                objectFit: 'cover',
                width: '100%',
                minWidth: `${MIN_WIDTH}px`,
                maxWidth: `${MAX_WIDTH}px`,
                height: `${HEIGHT}px`,
              }}
            />
          )}
          <div className="gradient-overlay overlay-dark transition duration-200"></div>
        </div>

        <div className="flex flex-col grow justify-end pt-3 px-6 pb-4">
          <div className="tour-title">
            <h3 className="mb-2" role="tour-title">
              {tour.title}
            </h3>
          </div>

          <div className="tour-content flex flex-col grow justify-end">
            <div className="tour-details border-b border-dotted border-gray-500">
              {feature &&
                <div className="flex flex-row items-center mb-2">
                  <div className="flex items-center w-4 mr-2">
                    {feature?.icon}
                  </div>
                  <p className="inline">Comfort: {feature?.title}</p>
                </div>
              }

              <div className="flex flex-row items-center mb-2">
                <div className="flex items-center w-4 mr-2">
                  <Image src={pin} alt="Destination" className="inline mr-2" />
                </div>
                <p className="inline">Destination: {tour.destinations.join(', ')}</p>
              </div>

              {primaryLanguage && (
                <div className="flex flex-row items-center mb-2">
                  <div className="flex items-center w-4 mr-2">
                    <Image src={message} alt="Languages spoken" className="inline mr-2" />
                  </div>
                  <p className="inline">
                    Operated in: {primaryLanguage}
                    {additionalCount > 0 && (
                      <span className="font-medium text-white">
                        {' '}
                        and {additionalCount} more
                      </span>
                    )}
                  </p>
                </div>
              )}

              <div className="flex flex-row items-center mb-2">
                <div className="flex items-center w-4 mr-2">
                  <Image src={clock} alt="Duration" className="inline mr-2" />
                </div>
                <p className="inline">Duration: {tour.duration}</p>
              </div>
            </div>

            <div className="tour-footer flex flex-row items-center justify-between pt-2">
              <p>
                {tour.lowestPrice != null && tour.lowestPrice > 0 ? (
                  <>
                    <span>Starts from </span>
                    <span className="h3">{format.money(tour.lowestPrice, tour.currency)}</span>
                  </>
                ) : (
                  <span className="h3">Price on request</span>
                )}
              </p>
              {!!tour.avarageReview && (
                <div className="flex direction-row bg-gray-800 items-center justify-center gap-1 px-1.5 py-0.5 rounded-full">
                  <Image src={star} alt="Rating" />
                  <span className="small">
                    {tour.avarageReview.toFixed(1)}
                    {tour.numberOfReviews > 0 ? ` - ${tour.numberOfReviews} Reviews` : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
